import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pager } from "./components/Pager";
import { nyVersjonVenter, paaNyVersjon, taINyVersjon } from "./state/oppdatering";
import { foelgViewport } from "./state/vindu";
import { SynkScreen } from "./screens/SynkScreen";
import { SpoersmaalScreen } from "./screens/SpoersmaalScreen";
import { OppsettSide } from "./screens/oppsett/OppsettSide";
import { ProfilScreen } from "./screens/ProfilScreen";
import { KonsollScreen } from "./screens/KonsollScreen";
import { valgtProduksjon, velgProduksjon } from "./state/konsoll";
import * as oppsett from "./state/oppsett";
import { useSidevalg } from "./state/sidevalg";
import * as synk from "./state/synk";
import { LiveloggScreen } from "./screens/LiveloggScreen";
import { AvsluttScreen } from "./screens/AvsluttScreen";
import { LoggScreen } from "./screens/LoggScreen";
import { RapporterScreen } from "./screens/RapporterScreen";
import { BriefScreen } from "./screens/BriefScreen";
import { ShotlistScreen } from "./screens/ShotlistScreen";
import { Tidskode } from "./components/Tidskode";
import { Tidskodevindu } from "./components/Tidskodevindu";
import { Prikker } from "./components/Prikker";
import { OpplastingScreen } from "./screens/OpplastingScreen";
import { hentOekt, spec, type Oekt } from "./state/session";
import { InnloggingScreen } from "./screens/InnloggingScreen";
import {
  erInne, hentOektFraServer, paaEndring, ryddAdressen, type Tilstand,
} from "./state/innlogging";
import { krevInnlogging } from "./state/supabase";
import { datoTekst, hentDager, type Opptaksdag } from "./state/opptaksdager";
import { useLevende } from "./state/levende";
import { OMRAADER, useOmraader, type Omraade } from "./state/omraade";
import { Meny } from "./components/Meny";
import { PREF_BLACKOUT, usePreference } from "./state/innstillinger";

/*
 * Tegneflata lastes FØRST NÅR FANEN ÅPNES.
 *
 * Den drar med seg et tegnebibliotek på et par hundre kilobyte, og de andre
 * leseflatene skal ikke betale for noe de ikke bruker. En koordinator som
 * laster opp en dagsplan har ingen glede av Konva.
 */

/**
 * Nettflata for prodleder, koordinator og redaktoer.
 *
 * VITE OG REACT, IKKE NEXT.JS — avvik fra docs/02-arkitektur.md.
 *
 * Begrunnelsen: flata er en ren klient. Det eneste som trenger en server er
 * utsending av rapport paa e-post, og arkitekturen peker alt paa Supabase Edge
 * Functions for den slags. Da staar Next.js igjen med ruting vi ikke trenger og
 * en byggkjede til paa Cloudflare Pages. Med Vite er det samme verktoey, samme
 * byggkommando og samme utrulling som feltappen, og tokens kan deles direkte.
 *
 * Si fra om dette skal snus — det er billigere naa enn naar det finnes skjermer.
 */

/*
 * Rekkefoelgen foelger HVEM SOM BRUKER FLATA, ikke hva den gjoer.
 *
 * Foerst det alle deler, saa kontor, saa innhold, saa teknisk. Da ligger ens
 * egne flater samlet uansett hvilke omraader man har slaatt paa — og den som
 * bare har ett omraade faar en kort, sammenhengende rekke i stedet for hull
 * mellom fanene.
 *
 * Filtreringen beholder denne rekkefoelgen, saa dette ene stedet avgjoer
 * alt: faner, prikker og sider.
 */
const SIDER = [
  /* Alle: her lander dagens dokumenter. */
  { id: "opplasting", navn: "Opplasting" },
  { id: "brief", navn: "Brief" },
  /* Kontor har ingen flate for seg selv ennaa. */
  /* Innhold. */
  { id: "logg", navn: "Logg" },
  /* Redaktoerens liste over hva som skal spoerres om. Het «Synk» til
     24.10.2026 — navnet flyttet til reporterens arbeidsflate, som er der
     spoersmaalene faktisk stilles. */
  { id: "spoersmaal", navn: "Spørsmål" },
  /*
   * DELTAKERNE HAR INGEN FANE HER LENGER.
   *
   * Brukeren 02.11.2026: «Vi kan fjerne "deltakere" flata fra
   * arbeidsflata. Det er bare informasjon. Vi har muligheten til aa se
   * og legge til/fjerne deltakere fra oppsetts-flaten. Det holder.»
   *
   * Hen har rett: lista var skrivebeskyttet her fra i gaar, og en fane
   * som bare viser noe man alt kan se et annet sted er en fane man blar
   * forbi hver dag resten av produksjonen. Forkortelsen er fortsatt det
   * regi skriver i loggen — den slaas opp der den brukes, ikke her.
   *
   * Lista ligger under Oppsett · Innhold, der den ogsaa kan endres.
   * `DeltakereScreen` lever videre; det er bare fanen som er borte.
   */
  { id: "rapporter", navn: "Rapporter" },
  /* Opptak: crewet som staar i det mens det skjer. */
  { id: "livelogg", navn: "Livelogg" },
  { id: "synk", navn: "Synk" },
  /* Teknisk. Shotlista hoerer hjemme her, selv om innhold ogsaa leser den. */
  { id: "shotlist", navn: "Shotlist" },
  /*
   * KAMERAPLANEN HAR INGEN FANE HER LENGER.
   *
   * Brukeren 02.11.2026: «Vi kan ogsaa flytte kameraplan til
   * oppsetts-siden. Den er det A-foto som bruker.» Den ligger naa som
   * fjerde fane under Oppsett — se screens/oppsett/OppsettSide.tsx.
   *
   * Det er riktig deling: tegneflata er noe man rigger med, ikke noe man
   * blar forbi paa en opptaksdag.
   */

] as const;

const ALLE_SIDEIDER: readonly string[] = SIDER.map((s) => s.id);

/**
 * PORTEN. Avgjoer HVEM flata er, foer flata finnes.
 *
 * Tre tilstander, og de er ikke varianter av hverandre:
 *
 * - «sjekker» — vi vet ikke ennaa. Varer et oeyeblikk, men flata maa ikke
 *   vise innloggingskortet i mellomtiden: da ville den som ER innlogget
 *   sett et skjema blinke hver gang hen laster siden.
 * - «ute» — ingen oekt. Innloggingskortet.
 * - «inne» — det finnes en konto. Men en konto er ikke en tilgang: oekten
 *   bygges av `production_member`, og har den innloggede ingen medlemskap,
 *   er hen ikke med i noen produksjon. Da hjelper ingen ny lenke, og flata
 *   sier det i stedet for aa vise en tom produksjon.
 *
 * ========  UTEN INNLOGGING VIRKER DET SOM FOER  ========
 *
 * `krevInnlogging` er av i `.env.local` mens flatene bygges, og da gjelder
 * froeet i `session.ts`. Det er ikke bare bekvemmelighet — se
 * `state/supabase.ts`: appen skal kunne BRUKES uten konto, og innlogging
 * hoerer til nettflata og de stoerre produksjonene.
 */
export function App(): JSX.Element {
  /*
   * PALETTEN BOR OVER PORTEN, ikke inne i flata.
   *
   * Tokenene er deklarert paa `:root`, saa det er der de maa overstyres — og
   * da gjelder de ogsaa for nettleserens egne flater gjennom `color-scheme`.
   * Feltappen gjoer det samme (`apps/mobile/src/App.tsx`), men har i tillegg
   * to iOS-hack for statuslinja som ikke hoerer hjemme her.
   *
   * At den staar HER og ikke i `Flate` er ikke tilfeldig: laa den der, ville
   * innloggingskortet vaert kritthvitt for en som har blackout paa. Det er
   * nettopp den skjermen man moeter midt paa natta, i et kontrollrom der
   * ingenting skal lyse opp settet.
   */
  const [blackout, setBlackout] = usePreference(PREF_BLACKOUT, false);
  useEffect(() => {
    document.documentElement.dataset["blackout"] = String(blackout);
  }, [blackout]);

  /* Flata faar hoeyden sin av den VISUELLE viewporten, saa toppstripa ikke
     dyttes ut naar tastaturet kommer opp paa en iPad. Se state/vindu.ts. */
  useEffect(foelgViewport, []);

  const [tilstand, setTilstand] = useState<Tilstand>(
    krevInnlogging ? "sjekker" : "inne");
  const [oekt, setOekt] = useState<Oekt | null>(
    () => (krevInnlogging ? null : hentOekt()));

  const les = useCallback((): void => {
    void (async () => {
      if (!(await erInne())) { setOekt(null); setTilstand("ute"); return; }
      ryddAdressen();
      /* Ingen valgt produksjon gir null, og da staar konsollen. Det er
         landingen, ikke en feiltilstand — se state/konsoll.ts. */
      setOekt(await hentOektFraServer(valgtProduksjon()));
      setTilstand("inne");
    })();
  }, []);

  /* Lenka lander paa flata, ikke paa et skjema. Derfor abonnerer vi:
     supabase-js plukker tokenet ut av adressen etter foerste tegning, og
     uten dette ville kortet staatt der til man lastet siden om selv. */
  useEffect(() => {
    if (!krevInnlogging) return;
    les();
    return paaEndring(les);
  }, [les]);

  if (tilstand === "sjekker") return <Sjekker />;
  if (tilstand === "ute") return <InnloggingScreen />;
  /* Innlogget, men ingen produksjon valgt — eller ingen aa velge. Begge
     deler er konsollen. `les()` etterpaa og ikke en gjetning: medlemskapet
     ble til i samme transaksjon som produksjonen. */
  if (oekt === null) {
    return (
      <KonsollScreen paaValgt={les}
                     blackout={blackout} onBlackout={setBlackout} />
    );
  }
  /* Noekkelen: bytter medlemskapet, bygges flata paa nytt i stedet for aa
     beholde dag, fane og hentede rader fra en annen produksjon. */
  return (
    <Flate key={oekt.member.id} grunnoekt={oekt}
           blackout={blackout} onBlackout={setBlackout}
           paaKonsoll={() => { velgProduksjon(null); les(); }} />
  );
}

/** Mens vi spoer om det finnes en oekt. Ingen spinner — det tar et blunk. */
function Sjekker(): JSX.Element {
  return (
    <div className="innlogging">
      <div className="innlogging__kort">
        <span className="innlogging__merknad">Et øyeblikk …</span>
      </div>
    </div>
  );
}

function Flate({ grunnoekt, blackout, onBlackout, paaKonsoll }: {
  readonly grunnoekt: Oekt;
  readonly blackout: boolean;
  onBlackout(v: boolean): void;
  /** Veien tilbake til konsollen, for aa bytte produksjon. */
  paaKonsoll(): void;
}): JSX.Element {
  const sp = useMemo(() => spec(grunnoekt), [grunnoekt]);

  /*
   * DAGEN MAN SER PAA, ikke noedvendigvis dagen det er.
   *
   * Hele flata foelger den: dokumenter, shotlist og kameraplaner hentes for
   * dagen som staar i oekta. Derfor ligger valget her og ikke i en av
   * flatene — ellers ville de vist hver sin dag.
   */
  /*
   * OMRAADET RYDDER, DET SPERRER IKKE.
   *
   * Alle kan bytte naar som helst, og «Alt» er alltid ett trykk unna. Se
   * `state/omraade.ts` og den ufravikelige regelen om at ingenting skjules
   * innenfor det som deles.
   */
  const [omraade, vekslOmraade] = useOmraader();
  const sidevalg = useSidevalg(grunnoekt, ALLE_SIDEIDER);

  /*
   * OMRAADET SETTER LISTA, DET FILTRERER DEN IKKE.
   *
   * Slaar man paa Teknisk, kommer teknisk sine sider fram. Slaar man det
   * av, gaar de bort — MEN BARE DE SOM INGEN ANNEN PAASLAATT OMRAADE VIL
   * HA. Brief hoerer baade Kontor og Teknisk til, og den skal ikke
   * forsvinne fordi man ryddet bort det tekniske.
   *
   * ========  INGEN VALGT BETYR ALT  ========
   *
   * Regelen har staatt siden omraadene kom: «Er ingen slaatt paa, vises
   * alt: en tom flate er aldri svaret paa at man har klikket feil.»
   *
   * Den gikk tapt da omraadene sluttet aa vaere selve filteret — da ble
   * «ingen omraader» til «ingen sider», og den som slo av det siste
   * omraadet satt igjen uten én eneste fane. Rettet 27.10.2026.
   */
  const vekslOmraadeOgSider = useCallback((o: Omraade, paa: boolean): void => {
    vekslOmraade(o, paa);

    /*
     * BRYTEREN SETTER HELE LISTA, den legger ikke til i den.
     *
     * Her sto `settMange(def.sider, true)` paa vei inn og en fradrags-
     * regning paa vei ut. Lista starter med ALT paa, saa aa legge til
     * la ikke til noe, og aa trekke fra trakk ikke fra noe saa lenge et
     * annet omraade delte sidene. Brukeren 01.11.2026: «Filtrene i
     * menyen under "område" er igjen ødelagt. De gjør ingenting med
     * utvalget av arbeidsflater.» Hen hadde rett: de gjorde bokstavelig
     * talt ingenting.
     *
     * Naa er lista unionen av de paaslaatte omraadene. Det er fortsatt
     * ETT filter — sidevalget — og omraadet er fortsatt bare en maate
     * aa sette det paa. Slaar man av det siste, vises alt, som regelen
     * sier.
     */
    const naa = new Set(omraade.paa);
    if (paa) naa.add(o); else naa.delete(o);
    if (naa.size === 0) { sidevalg.visAlle(); return; }
    sidevalg.settKun(
      [...new Set(OMRAADER.filter((x) => naa.has(x.id)).flatMap((x) => x.sider))]);
  }, [vekslOmraade, sidevalg, omraade.paa]);

  /*
   * FLATA HUSKES PAA ID, IKKE PAA PLASS I REKKA.
   *
   * Indeksen er en posisjon, og den betyr noe annet saa snart lista endrer
   * seg. Sto man paa Brief med bare Kontor paa — [Opplasting, Brief] — og
   * slo paa Innhold, ble plass 1 plutselig Synk, og flata hoppet.
   */
  const [sideId, setSideId] = useState("opplasting");

  /*
   * «Dette er den samme siden som kommer opp etter foerste innlogging.»
   *
   * Den aapner seg naar noe som MAA finnes ikke gjoer det — og bare naar
   * det er noe DU kan gjoere med det. En redaktoer skal ikke lande paa en
   * liste over ting prodleder har glemt.
   *
   * ÉN GANG, ikke hver gang. Har man vaert der, er det et svar. Noekkelen
   * er per produksjon og per medlem, fordi det er per person det er sant.
   */
  useEffect(() => {
    const noekkel = `infoflyt.oppsett.sett.${grunnoekt.production.id}.${grunnoekt.member.id}`;
    let avbrutt = false;
    void (async () => {
      try {
        if (localStorage.getItem(noekkel) !== null) return;
      } catch { /* Privat modus. Da spoer vi hver gang, og det er greit. */ }
      const mangler = await oppsett.hentMangler(grunnoekt);
      const mine = oppsett.minePunkter(grunnoekt.member.role, grunnoekt.member.extraRoles ?? []);
      if (avbrutt || !oppsett.angaarMeg(mangler, mine)) return;
      /* Lander paa den flata som faktisk mangler noe for DEG. */
      setFlate("oppsett");
      try { localStorage.setItem(noekkel, "1"); } catch { /* ingenting */ }
    })();
    return () => { avbrutt = true; };
  }, [grunnoekt]);

  /* Ny versjon klar. Se state/oppdatering.ts for hvorfor dette er synlig. */
  const [nyVersjon, setNyVersjon] = useState(nyVersjonVenter);
  useEffect(() => paaNyVersjon(() => { setNyVersjon(true); }), []);

  const [valgtDag, setValgtDag] = useState(grunnoekt.shootDay);
  const [dager, setDager] = useState<readonly Opptaksdag[]>([]);
  /**
   * Har brukeren bladd selv?
   *
   * ========  DAGEN KAN FLYTTE SEG UNDER FOETTENE  ========
   *
   * `grunnoekt` bygges paa nytt ved hver endring i innloggingen — og et
   * token fornyes omtrent hver time. Svarer `dagen_naa` noe annet da enn da
   * sida ble lastet (vinduet gikk ut, eller noen la inn en dag), sto
   * `valgtDag` stille mens oekten flyttet seg. Toppstripa byttet da
   * tidskoden mot datoen i graatt — det er det brukeren saa 02.11.2026:
   * «tidskoden i toppstripa er ikke lenger synlig» — og liveloggen skrev
   * videre paa en dag som ikke lenger var dagens.
   *
   * Har man bladd selv, staar man i ro. Det er et valg, og flata skal ikke
   * rykke det fra en som leser i gaar.
   */
  const bladd = useRef(false);
  useEffect(() => {
    if (!bladd.current) setValgtDag(grunnoekt.shootDay);
  }, [grunnoekt.shootDay]);

  /*
   * PRODUKSJONENS TIDSKODE, i toppstripa.
   *
   * Her sto et hardkodet froe, og toppstripa viste derfor en helt annen
   * tidskode enn den som ble lagret i loggen. Naa leses jammen — det nyeste
   * ankeret noen faktisk har satt. Jammer regi midt i skiftet, foelger
   * stripa etter paa neste runde. Rettet 25.10.2026.
   */
  /*
   * Hvem som kan sette produksjonens tidskode.
   *
   * Speiler `tilgang.jammer`: Teknisk-gruppa, A-foto, VJ-en som er sin
   * egen tekniker, produsenten og prodleder. Ikke regi — se CLAUDE.md.
   * Basen haandhever det uansett; dette er for at knappen ikke skal staa
   * der og feile.
   */
  const kanJamme = ((): boolean => {
    const alle = [grunnoekt.member.role, ...(grunnoekt.member.extraRoles ?? [])];
    return ["tekniker", "afoto", "vj", "produsent", "prodleder"]
      .some((r) => alle.includes(r as typeof alle[number]));
  })();
  const [setterTc, setSetterTc] = useState(false);

  const [anker, setAnker] = useState<synk.Anker | null>(null);
  const [ankertikk, setAnkertikk] = useState(0);
  useEffect(() => {
    let avbrutt = false;
    const les = (): void => {
      void (async () => {
        const a = await synk.jamFor(grunnoekt.production.id, valgtDag);
        if (!avbrutt) setAnker(a);
      })();
    };
    les();
    const id = window.setInterval(les, 10000);
    return () => { avbrutt = true; window.clearInterval(id); };
  }, [grunnoekt.production.id, valgtDag, ankertikk]);

  const lesDager = useCallback((): void => {
    void (async () => setDager(await hentDager(grunnoekt.production.id)))();
  }, [grunnoekt.production.id]);
  useEffect(lesDager, [lesDager]);

  /*
   * OPPTAKSDAGENE MAA FOELGE MED.
   *
   * Her sto bare oppslaget over, kjoert én gang per produksjon. Legger
   * prodleder til en dag under Oppsett og blar tilbake til arbeidsflata,
   * sto fortsatt dagene fra opprettelsen — og det ser ut som om dagen
   * ikke ble lagret. Hver eneste andre flate abonnerer paa endringene
   * sine; denne var glemt.
   *
   * Det er ikke bare pynt: dagpilene i toppstripa og datoen under
   * tidskoden leser den samme lista.
   */
  /* `log_session` er med fordi lista ogsaa baerer dager det finnes LOGG
     paa uten kalenderrad — se hentDager. Uten den dukket en slik dag
     ikke opp foer man lastet sida om. */
  useLevende("dager", ["shoot_day", "log_session"], lesDager);

  const oekt = useMemo(
    () => ({
      ...grunnoekt,
      shootDay: valgtDag,
      shootDate: dager.find((d) => d.dag === valgtDag)?.dato ?? null,
    }),
    [grunnoekt, valgtDag, dager]);

  /*
   * ETT FILTER, IKKE TO.
   *
   * Omraadene var filteret fram til 27.10.2026. Naa er sidevalget det
   * eneste som avgjoer hva som vises, og et omraade er en snarvei som
   * skrur sidene sine paa eller av. Med to filtre ville en bryter staatt
   * paa uten at sida kom fram, og da er det flata som lyver.
   */
  const sider = SIDER.filter((x) => sidevalg.paa.has(x.id));

  /* Forsvinner flata man sto paa — omraadet den hoerte til ble slaatt av —
     lander man paa den foerste. Ellers staar man i ro. */
  const funnet = sider.findIndex((x) => x.id === sideId);
  const index = funnet === -1 ? 0 : funnet;
  const velgSide = (i: number): void => {
    const id = sider[i]?.id;
    if (id !== undefined) setSideId(id);
  };

  const naa = dager.findIndex((d) => d.dag === valgtDag);
  const iDag = valgtDag === grunnoekt.shootDay;
  const datoNa = dager[naa]?.dato ?? null;
  /* Indeksen leses av den FORRIGE verdien, ikke av renderen. To raske trykk
     paa samme pil skal telle to dager, ikke én. */
  const bla = (steg: number): void => {
    bladd.current = true;
    setValgtDag((forrige) => {
      const i = dager.findIndex((d) => d.dag === forrige);
      return dager[i + steg]?.dag ?? forrige;
    });
  };
  const [menyAapen, setMenyAapen] = useState(false);

  /*
   * TO SIDER PAA OEVERSTE NIVAA.
   *
   * Brukeren: «saa gaar man inn og velger om man skal se den siden, eller
   * oppsett-siden som har produksjon, teknisk og innhold som felt».
   *
   * Arbeidsflata er én side med sine faner; oppsettet er den andre med
   * sine tre. Byttet skjer i menyen — ikke med en «Ferdig»-knapp, som var
   * problemet med den forrige loesningen.
   */
  const [flate, setFlate] = useState<"arbeid" | "oppsett" | "profil" | "avslutt">("arbeid");


  const lukkMeny = useCallback(() => setMenyAapen(false), []);

  return (
    <div className="app">

      <Meny
        aapen={menyAapen}
        oekt={oekt}
        blackout={blackout}
        omraade={omraade}
        onBlackout={onBlackout}
        onOmraade={vekslOmraadeOgSider}
        flate={flate}
        onFlate={setFlate}
        sider={SIDER}
        sidevalg={sidevalg}
        onLukk={lukkMeny}
        onKonsoll={paaKonsoll}
      />

      {/*
        * Skallet holder alt som skal DYTTES naar menyen kommer fram. Menyen
        * er soesken til det, ikke et overlegg over det — se Meny.tsx.
        */}
      <div className="skall">
      <div className="midt">
        <header className="toppstripe">
          {/* Knappen staar paa SAMME SIDE som menyen kommer fra. Merket laa
              her foer; det bor naa oeverst i menyen. */}
          <span className="toppstripe__side">
            <button
              type="button"
              className="menyknapp"
              aria-label={menyAapen ? "Lukk menyen" : "Meny"}
              aria-expanded={menyAapen}
              onClick={() => setMenyAapen(!menyAapen)}
            >
              <span
                className={`menu-icon${menyAapen ? " menu-icon--open" : ""}`}
                aria-hidden="true"
              >
                <span /><span /><span />
              </span>
            </button>
          </span>
          {/*
            * Pilene peker VEKK fra midten, og midten viser dagen man staar
            * paa: tidskoden naar det er i dag, ellers datoen i graatt.
            * Tidskoden er dagens klokke — den betyr ingenting for en dag som
            * ikke er i dag, og da skal den ikke staa der og se ekte ut.
            */}
          <button
            type="button"
            className="dagpil"
            onClick={() => bla(-1)}
            disabled={naa <= 0}
            aria-label="Forrige opptaksdag"
          >
            <span aria-hidden="true">←</span>
          </button>
          {iDag ? (
            /*
              * TIDSKODEN ER EN KNAPP for den som kan sette den.
              *
              * Samme sted som i appen: man trykker paa tallet. Kan man
              * ikke jamme, er det et tall og ingenting annet — en knapp
              * som bare sier nei er verre enn ingen knapp.
              */
            kanJamme ? (
              <button
                type="button"
                className="tidskode__knapp"
                onClick={() => setSetterTc(true)}
                title="Sett produksjonens tidskode"
              >
                <Tidskode
                  anker={anker}
                  framerate={oekt.production.framerate}
                  dropFrame={oekt.production.dropFrame}
                  spec={sp}
                />
              </button>
            ) : (
              <Tidskode
                anker={anker}
                framerate={oekt.production.framerate}
                dropFrame={oekt.production.dropFrame}
                spec={sp}
              />
            )
          ) : (
            /*
              * DATOEN ER EN KNAPP TILBAKE.
              *
              * Staar man paa en annen dag, er tidskoden borte — og den
              * forsvant for brukeren uten at hen visste hvorfor. Da maa
              * veien tilbake vaere det man ser paa: ett trykk paa datoen,
              * og man staar i dag igjen.
              */
            <button
              type="button"
              className="tidskode tidskode--dato tidskode--tilbake mono"
              onClick={() => { bladd.current = false; setValgtDag(grunnoekt.shootDay); }}
              title="Tilbake til dagens opptaksdag"
            >
              {datoNa === null ? `Dag ${valgtDag}` : datoTekst(datoNa)}
            </button>
          )}
          <button
            type="button"
            className="dagpil"
            onClick={() => bla(1)}
            disabled={naa < 0 || naa >= dager.length - 1}
            aria-label="Neste opptaksdag"
          >
            <span aria-hidden="true">→</span>
          </button>
          <span className="toppstripe__side toppstripe__side--hoeyre">
            {/*
              * NY VERSJON SKAL VAERE SYNLIG.
              *
              * En fane som staar aapen kjoerer den gamle bunten videre, og da
              * kan en knapp gjoere noe annet enn det den gjoer i versjonen
              * som er publisert. Det er ikke noe man kan se — derfor staar
              * det her, foran navnet, til man trykker.
              */}
            {nyVersjon ? (
              <button
                type="button"
                className="toppstripe__nyversjon"
                onClick={taINyVersjon}
              >
                Ny versjon · last inn
              </button>
            ) : null}
            {/* Navnet blir staaende. Det svarer paa «hvem er jeg» av samme
                grunn som produksjonsnavnet staar under tidskoden i appen.
                Rollebytteren er flyttet inn i menyen — den er stillas. */}
            <span className="toppstripe__hvem">{oekt.member.displayName}</span>
          </span>
        </header>

        {/* Konteksten du staar i, som «Satt 13:41» i appen. */}
        <p className="kontekst">
          {oekt.production.name} · Opptaksdag {oekt.shootDay}
        </p>

        {/*
          * Faner under tidskoden.
          *
          * Blaing med styreflata er en snarvei for dem som har den. Fanene er
          * veien som alltid finnes — ogsaa paa en PC med mus, der horisontal
          * rulling ikke er en gest man har.
          */}
        {flate !== "arbeid" ? null : (
        <nav className="faner" aria-label="Sider">
          {sider.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={`faner__fane${i === index ? " faner__fane--aktiv" : ""}`}
              aria-current={i === index}
              onClick={() => velgSide(i)}
            >
              {s.navn}
            </button>
          ))}
        </nav>
        )}
      </div>

      {/*
        * Flatene slaas opp paa ID, ikke paa plass i rekka.
        *
        * Omraadet tar bort faner, og med en posisjonsliste ville den tredje
        * fana vist den femte flata. ID-en er det eneste som holder naar
        * lista endrer seg.
        */}
      {!setterTc ? null : (
        <Tidskodevindu
          oekt={oekt}
          anker={anker}
          paaLukk={() => setSetterTc(false)}
          /* Toppstripa leser ankeret paa nytt med én gang, i stedet for
             aa vente paa de ti sekundene takten bruker. */
          paaSatt={() => setAnkertikk((n) => n + 1)}
        />
      )}

      {flate === "oppsett" ? <OppsettSide oekt={oekt} />
       : flate === "profil" ? <ProfilScreen oekt={oekt} />
       : flate === "avslutt"
         ? <AvsluttScreen oekt={oekt} iDag={iDag} paaArbeid={() => setFlate("arbeid")} />
       : (
      <>
      <Pager index={index} onIndexChange={velgSide}>
        {sider.map((side) => {
          switch (side.id) {
            case "opplasting":
              return <OpplastingScreen key={side.id} oekt={oekt} bokser={omraade.bokser} />;
            /* Ikke gjemt for andre roller. Alle medlemmer av en produksjon
               leser alt som ligger i den — rollen styrer skriving og
               oppsett, ikke lesing. Se CLAUDE.md. */
            case "spoersmaal":
              return <SpoersmaalScreen key={side.id} oekt={oekt} />;
            /* Logging fra nett. Samme logg som synken skriver i, og samme
               regel: ingen skriver i en annen manns logg. */
            case "livelogg":
              return <LiveloggScreen key={side.id} oekt={oekt} iDag={iDag} />;
            /* Reporterens arbeidsflate. Skriver til loggen, som Livelogg —
               se state/nettlogg.ts for hvorfor det er i orden. */
            case "synk":
              return <SynkScreen key={side.id} oekt={oekt} iDag={iDag} />;
            case "brief":
              return <BriefScreen key={side.id} oekt={oekt} felt={omraade.briefFelt} />;
            case "shotlist":
              return <ShotlistScreen key={side.id} oekt={oekt} />;
            /* Redaktoeren leser loggene som gaar. Hen skriver aldri i dem —
               se screens/LoggScreen.tsx. */
            case "logg":
              return <LoggScreen key={side.id} oekt={oekt} opptaksdag={oekt.shootDay} />;
            /* Leverte rapporter, én boks per regi. Se
               screens/RapporterScreen.tsx. */
            case "rapporter":
              return <RapporterScreen key={side.id} oekt={oekt} />;
            default: {
              /* Alle fanene i SIDER har naa en flate, saa `side` er `never`
                 her. Vakta staar igjen med vilje: legger noen til en fane
                 uten aa lage flata, sier typen fra under bygget — ikke
                 brukeren, med en tom side. */
              const ukjent: never = side;
              return ukjent;
            }
          }
        })}
      </Pager>

      <Prikker
        antall={sider.length}
        index={index}
        navn={sider.map((s) => s.navn)}
        onVelg={velgSide}
      />
      </>
      )}
      </div>

    </div>
  );
}

/** Ærlig plassholder. Bedre enn en flate som later som den virker. */
function IkkeBygd({ hva }: { readonly hva: string }): JSX.Element {
  return (
    <div className="flate">
      <div className="flate__innhold">
        <p className="tomtekst">Ikke bygd ennå.</p>
        <p className="merknad">{hva}</p>
      </div>
    </div>
  );
}
